import { VkOrdAgencyAct, VkOrdAgencyContract } from '@synapex/prisma/client'
import { WorkspaceDescriptor } from '@synapex/shared2/entities'
import {
    CreateVkOrdAgencyActDto,
    CreateVkOrdAgencyContractDto,
    UpdateVkOrdAgencyActDto,
    UpdateVkOrdAgencyContractDto,
    VkOrdAgencyContractResponse,
    VkOrdCreativeResponseWithCreative,
    WorkspaceDescriptorDto,
} from '@synapex/shared2/gateway-rest-api'
import {
    useMutation,
    useQuery,
    useQueryClient,
    UseQueryOptions,
} from '@tanstack/react-query'
import { apiQueryKey } from '../query-key'
import {
    createAgencyAct,
    createAgencyContract,
    deleteAgencyAct,
    deleteAgencyContract,
    getAgencyContracts,
    getAgencyCreatives,
    updateAgencyAct,
    updateAgencyContract,
} from './agency.service'

export function useGetAgencyContracts(
    wd: WorkspaceDescriptor,
    options?: Omit<
        UseQueryOptions<VkOrdAgencyContractResponse[], Error>,
        'queryKey'
    >
) {
    return useQuery<VkOrdAgencyContractResponse[], Error>({
        queryKey: apiQueryKey.agencyContracts(wd),
        queryFn: () => getAgencyContracts(wd),
        staleTime: 0,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        ...options,
    })
}

export function useDeleteAgencyContract(wd: WorkspaceDescriptor) {
    const queryClient = useQueryClient()

    return useMutation<
        unknown,
        Error,
        {
            contractId: number
        }
    >({
        mutationFn: async ({ contractId }) => {
            await deleteAgencyContract({ contractId })
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: apiQueryKey.agencyContracts(wd),
            })
        },
    })
}

export function useCreateAgencyContract(wd: WorkspaceDescriptor) {
    const queryClient = useQueryClient()

    return useMutation<
        VkOrdAgencyContract,
        Error,
        CreateVkOrdAgencyContractDto
    >({
        mutationFn: (contract) => createAgencyContract(wd, contract),
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: apiQueryKey.agencyContracts(wd),
            })
        },
    })
}

export function useUpdateAgencyContract(wd: WorkspaceDescriptor) {
    const queryClient = useQueryClient()

    return useMutation<
        VkOrdAgencyContract,
        Error,
        {
            contractId: number
            contract: UpdateVkOrdAgencyContractDto
        }
    >({
        mutationFn: ({ contractId, contract }) =>
            updateAgencyContract(
                {
                    ...wd,
                    contractId,
                },
                contract
            ),
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: apiQueryKey.agencyContracts(wd),
            })
        },
    })
}

export function useDeleteAgencyAct(wd: WorkspaceDescriptor) {
    const queryClient = useQueryClient()

    return useMutation<
        unknown,
        Error,
        {
            actId: number
        }
    >({
        mutationFn: async ({ actId }) => {
            await deleteAgencyAct({ actId })
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: apiQueryKey.agencyContracts(wd),
            })
            void queryClient.invalidateQueries({
                queryKey: apiQueryKey.agencyCreatives(),
            })
        },
    })
}

export function useCreateAgencyAct(wd: WorkspaceDescriptor) {
    const queryClient = useQueryClient()

    return useMutation<VkOrdAgencyAct, Error, CreateVkOrdAgencyActDto>({
        mutationFn: (act) => createAgencyAct(wd, act),
        onSuccess: (_, act) => {
            void queryClient.invalidateQueries({
                queryKey: apiQueryKey.agencyContracts(wd),
            })
            void queryClient.invalidateQueries({
                queryKey: apiQueryKey.agencyCreatives(act.agencyContractId),
            })
        },
    })
}

export function useUpdateAgencyAct(wd: WorkspaceDescriptorDto) {
    const queryClient = useQueryClient()

    return useMutation<
        VkOrdAgencyAct,
        Error,
        {
            actId: number
            act: UpdateVkOrdAgencyActDto
        }
    >({
        mutationFn: ({ actId, act }) =>
            updateAgencyAct(
                {
                    ...wd,
                    actId,
                },
                act
            ),
        onSuccess: (_, { act }) => {
            void queryClient.invalidateQueries({
                queryKey: apiQueryKey.agencyContracts(wd),
            })
            void queryClient.invalidateQueries({
                queryKey: apiQueryKey.agencyCreatives(act.agencyContractId),
            })
        },
    })
}

export function useGetAgencyCreatives(
    agencyContractId: number,
    options?: Omit<
        UseQueryOptions<VkOrdCreativeResponseWithCreative[], Error>,
        'queryKey' | 'queryFn'
    >
) {
    return useQuery<VkOrdCreativeResponseWithCreative[], Error>({
        ...options,
        queryKey: apiQueryKey.agencyCreatives(agencyContractId),
        queryFn: () => getAgencyCreatives(agencyContractId),
        ...options,
    })
}
